// Custom React Hook for Order Management
import { useState, useEffect } from 'react';
import { 
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  Unsubscribe
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';

interface Order {
  id: string;
  state: string;
  shopperId: string;
  businessId: string;
  driverId?: string;
  items: any[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  deliveryAddress?: {
    street?: string;
    line1?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    zip?: string;
    lat?: number;
    lng?: number;
  };
  customerName?: string;
  customerPhone?: string;
  trackingUrl?: string;
  // Uber Direct delivery data (populated by webhooks)
  uber?: {
    quoteId?: string;
    quoteFee?: number;
    quoteDropoffEta?: string;
    deliveryId?: string;
    deliveryStatus?: string;
    trackingUrl?: string;
    pickupEta?: string;
    dropoffEta?: string;
    batchId?: string | null;
    dropoffSequence?: number;
    totalDropoffCount?: number;
    courier?: {
      name?: string;
      phone?: string;
      vehicleType?: string;
      imgUrl?: string;
    };
    courierLocation?: {
      lat: number;
      lng: number;
      updatedAt?: any;
    };
    dispatchError?: string;
  };
  // Inventory tracking (managed by Cloud Functions)
  inventory?: {
    reservationIds: string[];
    reservedAt?: Date;
    committedAt?: Date;
    releasedAt?: Date;
    status: 'none' | 'reserved' | 'committed' | 'released' | 'partially_returned';
  };
  // Return tracking (managed by Cloud Functions)
  returns?: {
    hasActiveReturn: boolean;
    returnRequestIds: string[];
    totalRefunded: number;     // Running total in cents
  };
  // Payment tracking
  paymentIntentId?: string;
  payment?: {
    status: 'pending_authorization' | 'authorized' | 'captured' |
            'capture_failed' | 'voided' | 'refunded';
    amount?: number;
    currency?: string;
    authorizedAt?: Date;
    capturedAt?: Date;
    capturedAmount?: number;
    captureError?: string;
    refundId?: string;
    refundedAmount?: number;
    refundedAt?: Date;
    refundError?: string;
    voidedAt?: Date;
    voidError?: string;
  };
  flags?: {
    uberCancelled?: boolean;
    dispatchFailed?: boolean;
    needsAdminReview?: boolean;
    paymentCaptureFailed?: boolean;
    voidFailed?: boolean;
    refundFailed?: boolean;
  };
  cancellationReason?: string;
  deliveredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface TransitionRequest {
  orderId: string;
  action: string;
  actorId: string;
  actorRole: 'shopper' | 'business' | 'driver' | 'admin';
  clientRequestId?: string;
  metadata?: Record<string, any>;
}

export function useOrders(userId: string, role: 'shopper' | 'business' | 'driver') {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let unsubscribe: Unsubscribe;

    try {
      // Build query based on role
      let q;
      if (role === 'shopper') {
        q = query(
          collection(db, 'orders'),
          where('shopperId', '==', userId),
          orderBy('createdAt', 'desc')
        );
      } else if (role === 'business') {
        q = query(
          collection(db, 'orders'),
          where('businessId', '==', userId),
          where('state', 'in', ['PENDING_BUSINESS_APPROVAL', 'BUSINESS_APPROVED', 'DRIVER_ASSIGNED']),
          orderBy('createdAt', 'desc')
        );
      } else if (role === 'driver') {
        q = query(
          collection(db, 'orders'),
          where('driverId', '==', userId),
          orderBy('createdAt', 'desc')
        );
      } else {
        throw new Error('Invalid role');
      }

      // Subscribe to real-time updates
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const ordersData: Order[] = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Order[];
          
          setOrders(ordersData);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching orders:', err);
          setError(err.message);
          setLoading(false);
        }
      );
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId, role]);

  return {
    orders,
    loading,
    error,
  };
}

// Hook for single order with real-time updates
export function useOrder(orderId: string | null) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      (doc) => {
        if (doc.exists()) {
          setOrder({ id: doc.id, ...doc.data() } as Order);
        } else {
          setOrder(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching order:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId]);

  return {
    order,
    loading,
    error,
  };
}

// Hook for order state transitions
export function useOrderTransition() {
  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transitionOrder = async (request: Omit<TransitionRequest, 'clientRequestId'>) => {
    setTransitioning(true);
    setError(null);

    try {
      const transitionFn = httpsCallable(functions, 'transitionOrder');
      
      const result = await transitionFn({
        ...request,
        clientRequestId: uuidv4(), // Generate unique ID for idempotency
      });

      setTransitioning(false);
      return result.data;
    } catch (err: any) {
      console.error('Order transition error:', err);
      setError(err.message);
      setTransitioning(false);
      throw err;
    }
  };

  return {
    transitionOrder,
    transitioning,
    error,
  };
}

